import React from 'react';
import { css, styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorTextColorIcon from '@atlaskit/icon/core/migration/layout-one-column--editor-layout-single';
import EditorLayoutThreeEqualIcon from '@atlaskit/icon/core/migration/layout-three-columns--editor-layout-three-equal';
import EditorLayoutTwoEqualIcon from '@atlaskit/icon/core/migration/layout-two-columns--editor-layout-two-equal';
import EditorLayoutTwoLeftSidebarIcon from '@atlaskit/icon/core/migration/layout-two-columns-sidebar-left--editor-layout-two-left-sidebar';
import EditorLayoutTwoRightSidebarIcon from '@atlaskit/icon/core/migration/layout-two-columns-sidebar-right--editor-layout-two-right-sidebar';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { LAYOUTS } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';
import type { LayoutPickerProps } from './types.tsx';

export const LayoutPicker = ({ onLayoutSelected, activeLayout }: LayoutPickerProps) => {
	const { formatMessage } = useIntl();

	const layoutComponents = {
		A: EditorTextColorIcon,
		AA: EditorLayoutTwoEqualIcon,
		AAA: EditorLayoutThreeEqualIcon,
		BA: EditorLayoutTwoLeftSidebarIcon,
		AB: EditorLayoutTwoRightSidebarIcon,
	};
	const { oneColumn, twoColumns, threeColumns, leftSidebar, rightSidebar } = messages;
	const tooltips = {
		A: oneColumn,
		AA: twoColumns,
		AAA: threeColumns,
		BA: leftSidebar,
		AB: rightSidebar,
	};

	return (
		<Box xcss={pickerWrapperStyles} role="radiogroup">
			{LAYOUTS.map((layout) => {
				const Layout = layoutComponents[layout];
				const isSelected = layout === activeLayout;
				const label = formatMessage(tooltips[layout]);
				return (
					<LayoutWrapper key={layout} isSelected={isSelected}>
						<Tooltip content={label} hideTooltipOnClick position="bottom">
							<ButtonStyled
								testId={`dashboard-edit.ui.header-editable.change-layout-button.layout-picker.label.${layout}`}
								id={`layout-${layout}`}
								role="radio"
								appearance="subtle"
								spacing="default"
								isSelected={isSelected}
								aria-checked={isSelected}
								onClick={() => onLayoutSelected(layout)}
								iconBefore={<Layout label={label} />}
							/>
						</Tooltip>
					</LayoutWrapper>
				);
			})}
		</Box>
	);
};

const pickerWrapperStyles = xcss({
	padding: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LayoutWrapper = styled.div<{ isSelected: boolean }>(
	{
		display: 'inline-block',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSelected }) =>
		isSelected
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
					button: {
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						backgroundColor: token('color.background.selected'),
					},
				})
			: undefined,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonStyled = styled(Button)({
	'&:focus': {
		outlineOffset: token('space.negative.025'),
	},
});
